/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';

export interface MultiAuthConfirmationDialogProps {
  onAction?: (result: boolean) => void;
}

/**
 * Displays a confirmation dialog to the user asking about
 * enabling multi-factor authentication on an Account.
 *
 * ```typescript
 *
 * import MultiAuthConfirmationDialog from './MultiAuthConfirmationDialog';
 *
 * <MultiAuthConfirmationDialog onAction={onAction} />
 *
 * ```
 */
export const MultiAuthConfirmationDialog = ({
  onAction
}: MultiAuthConfirmationDialogProps) => {
  const onDialogAction = useCallback((confirmed: boolean) => function() {
    onAction?.(confirmed);
  }, [onAction]);

  return (
    <UserNotificationDialog dismissLabel={translate('common:editors.shared.cancel')}
      actionLabel={translate('admin-groups:dialog.multiAuth.confirmAction')}
      title={translate('admin-groups:dialog.multiAuth.confirmTitle')}
      message={translate('admin-groups:dialog.multiAuth.confirmMessage')}
      onDismiss={onDialogAction(false)}
      onAction={onDialogAction(true)}
      allowWhitespace
      curvedCorners
      capitalize
      show />
  );
};

export default memo(MultiAuthConfirmationDialog);
